import React from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAdaptiveSmartCardVariant } from '../../../hooks/use-adaptive-smart-card-variant';
import type { LayoutRendererProps } from '../types';
import { getElementKey } from '../utils';

/**
 * Responsive two-column layout styles using CSS flexbox with wrap.
 *
 * This approach uses pure CSS for responsiveness:
 * - flexWrap: 'wrap' allows columns to stack when container is narrow
 * - minWidth on columns controls the wrap breakpoint
 * - No JavaScript polling or ResizeObserver needed for layout changes
 *
 * Both columns have equal weight (flexGrow: 1, flexBasis: '0%') so they share
 * available space equally. Each column has minWidth: '300px', so when the
 * container width is less than ~600px (2 * 300px) plus gap, the columns
 * will naturally wrap to stack vertically.
 */
const styles = cssMap({
	container: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: token('space.200'),
		width: '100%',
	},
	leftColumn: {
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '0%',
		// When container is too narrow, this minWidth forces wrapping
		// Using a CSS custom property that can be set at build time
		minWidth: '300px',
		// Ensure children (e.g. SmartLink cards) stretch to fill the column width
		display: 'flex',
		flexDirection: 'column',
	},
	rightColumn: {
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '0%',
		minWidth: '300px',
		// Ensure children (e.g. SmartLink cards) stretch to fill the column width
		display: 'flex',
		flexDirection: 'column',
	},
});

/**
 * Two-columns layout component
 * Renders elements in a left and right column with adaptive smart card sizing.
 *
 * Uses CSS flexbox with wrap for responsive behavior:
 * - At wide widths: side-by-side columns
 * - At narrow widths: automatically stacks vertically via CSS flexWrap
 *
 * No JavaScript-based width detection is used for the layout change,
 * making this more performant and avoiding layout shifts.
 */
export const TwoColumnsLayout: React.MemoExoticComponent<
	(props: LayoutRendererProps) => React.JSX.Element | null
> = React.memo(
	(props: LayoutRendererProps): React.JSX.Element | null => {
		const {
			textContentRef,
			leftNeedsMeasurement,
			rightNeedsMeasurement,
			leftElements,
			rightElements,
			cardVariant,
		} = useAdaptiveSmartCardVariant(props.content);

		if (props.content.length < 2) {
			return null;
		}

		return (
			<Box xcss={styles.container} testId="two-column-layout-container">
				<Box
					xcss={styles.leftColumn}
					ref={leftNeedsMeasurement ? textContentRef : undefined}
					testId="two-column-layout-left"
				>
					{leftElements.map((element, index) => (
						<React.Fragment
							key={getElementKey({ key: props.sectionKey, pos: element.attributes?.pos, index })}
						>
							{props.renderElement({
								component: element,
								position: 'two-columns-left',
								variant: cardVariant,
								isFoldable: props.isFoldable,
							})}
						</React.Fragment>
					))}
				</Box>
				<Box
					xcss={styles.rightColumn}
					ref={rightNeedsMeasurement ? textContentRef : undefined}
					testId="two-column-layout-right"
				>
					{rightElements.map((element, index) => (
						<React.Fragment
							key={getElementKey({ key: props.sectionKey, pos: element.attributes?.pos, index })}
						>
							{props.renderElement({
								component: element,
								position: 'two-columns-right',
								variant: cardVariant,
								isFoldable: props.isFoldable,
							})}
						</React.Fragment>
					))}
				</Box>
			</Box>
		);
	},
	(prevProps, nextProps) => {
		return (
			prevProps.content === nextProps.content &&
			prevProps.isFoldable === nextProps.isFoldable &&
			prevProps.isStreaming === nextProps.isStreaming &&
			prevProps.subAgentDataMap === nextProps.subAgentDataMap
		);
	},
);
